// Composeur de messages internes : WYSIWYG (Tiptap) + destinataires (tous / un membre)
// + choix du pôle expéditeur + confirmation avant envoi en masse. Historique en bas.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, Users, User, Search, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import RichTextEditor from "@/components/RichTextEditor";
import { UserAvatar } from "@/components/UserAvatar";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { POLES } from "@/lib/constants";
import { formatJour } from "@/lib/dates";

export function peutComposer(user) {
  if (!user) return false;
  return ["admin", "ca"].includes(user.role) || Boolean(user.pole_responsable);
}

const CartePreviewDestinataire = ({ mode, count, poleLabel, testId }) => (
  <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm" data-testid={testId}>
    {mode === "all" ? <Users className="h-4 w-4 text-primary" /> : <User className="h-4 w-4 text-primary" />}
    <div className="flex-1">
      <p className="font-medium">
        {mode === "all" ? `${count} membres actifs` : "1 membre"}
      </p>
      <p className="text-xs text-muted-foreground">
        Au nom de <strong className="text-foreground">{poleLabel}</strong>
      </p>
    </div>
  </div>
);

export default function Messages() {
  const { user } = useAuth();
  const [polesAutorises, setPolesAutorises] = useState([]);
  const [membres, setMembres] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [chargement, setChargement] = useState(false);

  // Formulaire
  const [titre, setTitre] = useState("");
  const [messageHtml, setMessageHtml] = useState("");
  const [pole, setPole] = useState("");
  const [mode, setMode] = useState("all"); // "all" | "one"
  const [destinataireEmail, setDestinataireEmail] = useState("");
  const [rechercheOuverte, setRechercheOuverte] = useState(false);

  // Confirmation
  const [confirmationOuverte, setConfirmationOuverte] = useState(false);

  const acces = peutComposer(user);
  const nbMembresActifs = useMemo(() => membres.filter((m) => m.statut === "actif").length, [membres]);
  const poleVerrouille = user?.role !== "admin" && user?.role !== "ca";
  const poleLabel = pole ? POLES[pole] : "—";
  const destinataireChoisi = membres.find((m) => m.email === destinataireEmail);

  const chargerTout = useCallback(async () => {
    try {
      const [p, u, h] = await Promise.all([
        api.get("/messages/poles"),
        api.get("/users"),
        api.get("/messages"),
      ]);
      setPolesAutorises(p.data);
      setMembres(u.data);
      setHistorique(h.data);
      // Initialise le pôle : si l'utilisateur est responsable, verrouille sur son pôle
      if (user.pole_responsable && !pole) setPole(user.pole_responsable);
      else if (p.data.length === 1 && !pole) setPole(p.data[0]);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [user.pole_responsable, pole]);

  useEffect(() => {
    if (acces) chargerTout();
  }, [acces, chargerTout]);

  if (!acces) {
    return (
      <p className="text-center py-24 text-muted-foreground" data-testid="messages-forbidden">
        Cette page est réservée aux administrateurs, au CA et aux responsables de pôle.
      </p>
    );
  }

  const validerFormulaire = () => {
    if (!titre.trim()) return "Le titre est obligatoire.";
    if (!messageHtml.trim() || messageHtml === "<p></p>") return "Le corps du message est vide.";
    if (!pole) return "Choisissez un pôle expéditeur.";
    if (mode === "one" && !destinataireEmail) return "Choisissez un destinataire.";
    return null;
  };

  const soumettre = (e) => {
    e?.preventDefault?.();
    const erreur = validerFormulaire();
    if (erreur) return toast.error(erreur);
    setConfirmationOuverte(true);
  };

  const envoyerReellement = async () => {
    setChargement(true);
    try {
      await api.post("/messages", {
        title: titre.trim(),
        message_html: messageHtml,
        pole,
        recipients_mode: mode,
        recipient_email: mode === "one" ? destinataireEmail : null,
      });
      toast.success(mode === "all"
        ? `Message envoyé à ${nbMembresActifs} membres au nom de ${POLES[pole]}`
        : `Message envoyé à ${destinataireEmail}`
      );
      setTitre(""); setMessageHtml(""); setDestinataireEmail("");
      if (!poleVerrouille) setPole("");
      setConfirmationOuverte(false);
      chargerTout();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setChargement(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="messages-page">
      <div>
        <h1 className="font-serif text-4xl font-bold">Messages</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Composez et envoyez un message par email — au nom d'un pôle, à un membre ou à toute l'équipe.
        </p>
      </div>

      {/* ——— Composeur ——— */}
      <form onSubmit={soumettre} className="bg-card rounded-2xl border border-border p-6 md:p-8 space-y-6 carte-douce" data-testid="messages-composer">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Pôle expéditeur */}
          <div>
            <Label>Envoyer au nom du pôle *</Label>
            <Select value={pole} onValueChange={setPole} disabled={poleVerrouille}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="composer-pole-select">
                <SelectValue placeholder="Choisir un pôle…" />
              </SelectTrigger>
              <SelectContent>
                {polesAutorises.map((p) => (
                  <SelectItem key={p} value={p}>{POLES[p]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {poleVerrouille && (
              <p className="text-xs text-muted-foreground mt-1.5 italic">
                Verrouillé sur votre pôle de responsabilité.
              </p>
            )}
          </div>

          {/* Destinataires */}
          <div>
            <Label>Destinataires *</Label>
            <div className="flex gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => setMode("all")}
                className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                  mode === "all" ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"
                }`}
                data-testid="composer-mode-all"
              >
                <Users className="h-4 w-4 inline mr-1.5" /> Tous les membres
              </button>
              <button
                type="button"
                onClick={() => setMode("one")}
                className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                  mode === "one" ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"
                }`}
                data-testid="composer-mode-one"
              >
                <User className="h-4 w-4 inline mr-1.5" /> Un membre
              </button>
            </div>
            {mode === "one" && (
              <Popover open={rechercheOuverte} onOpenChange={setRechercheOuverte}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-muted transition-colors"
                    data-testid="composer-recipient-picker"
                  >
                    <Search className="h-4 w-4 text-muted-foreground" />
                    {destinataireChoisi ? (
                      <span className="flex items-center gap-2">
                        <UserAvatar user={destinataireChoisi} size="sm" />
                        <span>{destinataireChoisi.name} · {destinataireChoisi.email}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Rechercher un membre…</span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[380px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Nom ou email…" data-testid="composer-recipient-search" />
                    <CommandList>
                      <CommandEmpty>Aucun membre trouvé.</CommandEmpty>
                      <CommandGroup>
                        {membres.filter((m) => m.statut === "actif").map((m) => (
                          <CommandItem
                            key={m.id}
                            value={`${m.name} ${m.email}`}
                            onSelect={() => { setDestinataireEmail(m.email); setRechercheOuverte(false); }}
                            data-testid={`composer-recipient-option-${m.email}`}
                          >
                            <UserAvatar user={m} size="sm" />
                            <div className="ml-2">
                              <p className="text-sm">{m.name}</p>
                              <p className="text-xs text-muted-foreground">{m.email}</p>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Titre */}
        <div>
          <Label>Titre du message *</Label>
          <Input
            required
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            className="rounded-xl mt-1.5"
            placeholder="Ex. Répétition supplémentaire samedi"
            data-testid="composer-title-input"
          />
        </div>

        {/* Corps WYSIWYG */}
        <div>
          <Label>Corps du message *</Label>
          <div className="mt-1.5">
            <RichTextEditor
              value={messageHtml}
              onChange={setMessageHtml}
              placeholder="Écrivez votre message… (gras, liens, listes…)"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Éléments autorisés : gras, italique, souligné, titres H3, listes, citations, liens.
            Le HTML est nettoyé côté serveur avant envoi.
          </p>
        </div>

        {/* Résumé */}
        {pole && (
          <CartePreviewDestinataire
            mode={mode}
            count={nbMembresActifs}
            poleLabel={poleLabel}
            testId="composer-preview"
          />
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={chargement} className="rounded-full gap-1.5" data-testid="composer-submit">
            <Send className="h-4 w-4" /> Envoyer
          </Button>
        </div>
      </form>

      {/* ——— Dialogue de confirmation ——— */}
      <Dialog open={confirmationOuverte} onOpenChange={setConfirmationOuverte}>
        <DialogContent data-testid="composer-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Confirmer l'envoi</DialogTitle>
            <DialogDescription>
              {mode === "all" ? (
                <>Vous êtes sur le point d'envoyer <strong className="text-foreground">« {titre} »</strong> à <strong className="text-foreground">{nbMembresActifs} membres actifs</strong> au nom de <strong className="text-foreground">{poleLabel}</strong>. Cette action est irréversible une fois validée.</>
              ) : (
                <>Vous êtes sur le point d'envoyer <strong className="text-foreground">« {titre} »</strong> à <strong className="text-foreground">{destinataireEmail}</strong> au nom de <strong className="text-foreground">{poleLabel}</strong>.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmationOuverte(false)} className="rounded-full" data-testid="composer-confirm-cancel">
              Annuler
            </Button>
            <Button onClick={envoyerReellement} disabled={chargement} className="rounded-full gap-1.5" data-testid="composer-confirm-send">
              <Send className="h-4 w-4" /> {chargement ? "Envoi…" : "Confirmer et envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ——— Historique ——— */}
      <div className="space-y-3">
        <h2 className="font-serif text-2xl font-semibold">Historique</h2>
        {historique.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">Aucun message envoyé pour l'instant.</p>
        )}
        {historique.map((m) => (
          <details key={m.id} className="bg-card rounded-2xl border border-border p-5 carte-douce group" data-testid={`message-row-${m.id}`}>
            <summary className="cursor-pointer flex flex-wrap items-center gap-3 list-none">
              <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{m.title}</p>
                <p className="text-xs text-muted-foreground">
                  {formatJour(m.created_at)} · {POLES[m.pole]} · par {m.envoye_par?.name}
                </p>
              </div>
              <Badge variant="outline" className="text-xs">
                {m.recipients_mode === "all" ? `${m.recipients_count} membres` : "1 membre"}
              </Badge>
            </summary>
            <div className="mt-4 pt-4 border-t border-border grid gap-4 md:grid-cols-3">
              <div className="md:col-span-2">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Corps du message</p>
                <div className="prose-inyange text-sm" dangerouslySetInnerHTML={{ __html: m.message_html }} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Destinataires ({m.recipients_count})</p>
                <div className="max-h-48 overflow-y-auto text-xs font-mono space-y-0.5">
                  {m.recipients.map((r) => <p key={r}>{r}</p>)}
                </div>
              </div>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
